import React, {
  FC,
  useEffect,
  useState,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { useNavigate, useLocation } from "react-router";
import { Box, useMediaQuery, useTheme, Fade } from "@mui/material";
import { observer } from "mobx-react";

import { useAppStore } from "../hooks/useAppStore";
import { useChannels } from "../contexts/ChannelsContext";
import { websocketService } from "../services/websocketService";
import { ChatSearchResult } from "../services/apiService";
import ChatSearch from "./ui/ChatSearch";
import SidebarLogoContent from "./ui/sidebar/SidebarLogoContent";
import ResizableDrawerWrapper from "./ui/sidebar/ResizableDrawerWrapper";
import AdaptiveDrawer from "./ui/sidebar/AdaptiveDrawer";
import {
  SIDEBAR_DEFAULT_WIDTH,
  SIDEBAR_MIN_WIDTH,
  SIDEBAR_TAB_TYPES,
} from "../types/sidebar";
import SidebarFooterContent from "./ui/sidebar/SidebarFooterContent";
import ChatListRenderer from "./ui/sidebar/ChatListRenderer";
import SharedChatsContent from "./ui/sidebar/SharedChatsContent";
import SearchResultsRenderer from "./ui/sidebar/SearchResultsRenderer";
import SidebarClaimFilterChip from "./ui/sidebar/SidebarClaimFilterChip";
import { VirtualizedChatListHandle } from "./ui/sidebar/chatVirtualizedTree";

import { useChatStore } from "../hooks/useChatStore";
import { useMainStore } from "../hooks/useMainStore";
import SidebarFileViewer from "./ui/sidebar/SidebarFileViewer";
import { WS_EVENTS } from "../types/wsEvents";
import { useChatCategoryStore } from "../hooks/useChatCategoryStore";
import { useClaimsStore } from "../hooks/useClaimsStore";
import { IChannel } from "../services";
import ShareChatModal from "./ui/sidebar/modals/ShareChatModal";
import DeleteChatConfirmationDialog from "./ui/sidebar/modals/DeleteChatConfirmationDialog";
import CloseChatConfirmationDialog from "./ui/sidebar/modals/CloseChatConfirmationDialog";
import RenameChatDialog from "./ui/sidebar/modals/RenameChatDialog";
import { useAuth } from "../contexts/AuthContext";
import SidebarChatTabs from "./ui/sidebar/SidebarChatTabs";
import { useCapabilities } from "../contexts/permissions/useCapabilities";
import { getChannelConfigForChat } from "../config/channels";
import { SidebarFilterAction } from "./ui/sidebar/filter-components/SidebarFilterAction";
import { SidebarFilterPanel } from "./ui/sidebar/filter-components/SidebarFilterPanel";
import Row from "./ui/core/Row";
import {
  applySidebarFilters,
  isDefaultSidebarFilters,
  matchesClosedFilter,
} from "./helpers/sidebarFilterHelper";
import {
  clearPersistedClaimFilter,
  loadPersistedClaimFilter,
  savePersistedClaimFilter,
} from "./helpers/claimFilterStorage";

const CHAT_LIST_EMPTY_STATE_COPY = {
  pinned: {
    title: "No pinned chats yet",
    subtitle: "Pin a chat to keep it easy to find.",
  },
  all: {
    title: "No chats here yet",
    subtitle: "Your owned chats will appear here.",
  },
} as const;

const Sidebar: FC = observer(() => {
  const { user } = useAuth();
  const chatStore = useChatStore();
  const { filtersScope } = useMainStore();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const chatListRef = useRef<HTMLDivElement>(null);
  const searchResultsScrollRef = useRef<HTMLDivElement>(null);
  const sharedChatsRef = useRef<VirtualizedChatListHandle>(null);
  const chatListScrollRef = useRef<VirtualizedChatListHandle>(null);
  const { showHome } = useCapabilities();

  const {
    isCollapsed,
    isResizing,
    isSidebarMobile,
    setCollapsed,
    setSidebarWidth,
    toggleSidebar,
    searchQuery,
    setContractToPreview,
    setPolicyToPreview,
    isHomeV2Enabled,
  } = useAppStore();
  const {
    channels: chatChannels,
    loading,
    loadingMore,
    filterByClaimId: _filterByClaimId,
    deleteChannel,
    renameChannel,
    toggleChannelPin,
    closeChannel,
    toggleChannelShared,
    updateChannelTitle,
    reorderChannels,
    sidebarSelectedTab,
    updateSidebarSelectedTab,
    setFilterByClaimId,
  } = useChannels();
  const { clearChatCategoryStore, setIsPoliciesDocumentsDrawerOpen } =
    useChatCategoryStore();
  const {
    setTempClaimNumber,
    setClaimActivePredefaultQuestion,
    setClaimToPreview,
  } = useClaimsStore();

  const [wasExpandedByHover, setWasExpandedByHover] = useState(false);
  const [chatNotifications, setChatNotifications] = useState<Set<string>>(
    new Set(),
  );
  // Controls whether the active claim filter is actually applied to the chat
  // list, independent of which claim it targets. The chip's X clears this
  // without dropping filterByClaimId itself, so the chip stays visible and
  // clicking it re-applies the same claim's filter.
  const [isClaimFilterApplied, setIsClaimFilterApplied] = useState(true);

  const isVisible = !isCollapsed && !isSidebarMobile;
  const filterByClaimId = chatStore.claimModeEnabled ? _filterByClaimId : null;

  // While a persisted filter is being restored on mount (below), the "always
  // start applied" reset effect must not stomp the persisted applied/cleared
  // state before filterByClaimId has actually caught up to it.
  const isRestoringClaimFilterRef = useRef(false);
  const restoredClaimIdRef = useRef<string | null>(null);

  // Restore the claim filter chip's last state on first load, so refreshing
  // the page while a claim filter is active doesn't silently drop it.
  useEffect(() => {
    const persisted = loadPersistedClaimFilter(filtersScope);
    if (!persisted) {
      return;
    }

    isRestoringClaimFilterRef.current = true;
    restoredClaimIdRef.current = persisted.claimId;
    chatStore.setClaimModeEnabled(true);
    setFilterByClaimId(persisted.claimId);
    setIsClaimFilterApplied(persisted.isApplied);
    // Only ever restore once, on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // A newly set claim filter (new claim selected, chat loaded, etc.) should
  // start applied rather than inheriting a previous "cleared" state — unless
  // this change is filterByClaimId catching up to the mount-time restore
  // above, whose own applied/cleared state must be left alone.
  useEffect(() => {
    if (isRestoringClaimFilterRef.current) {
      if (filterByClaimId === restoredClaimIdRef.current) {
        // filterByClaimId has caught up to the mount-time restore; its own
        // applied/cleared choice (set above) stands.
        isRestoringClaimFilterRef.current = false;
        return;
      }
      if (!filterByClaimId) {
        // Still waiting for the restore to land — don't force "applied" in
        // the meantime, or it'll stomp a persisted "cleared" state below.
        return;
      }
      // Superseded by a real claim change before the restore landed.
      isRestoringClaimFilterRef.current = false;
    }
    setIsClaimFilterApplied(true);
  }, [filterByClaimId]);

  // Keep the persisted claim filter in sync with what's actually applied, so
  // re-applying the chip or switching claims survives a refresh too. Only
  // ever written (never cleared) when filterByClaimId itself goes null — it
  // does that for plenty of routine reasons that aren't "forget this claim":
  // leaving a chat (ChatSection's unmount cleanup), navigating via the Header
  // nav (its shared clearData/resetStore helpers), deleting the active chat —
  // and this is the one thing every one of those paths should leave alone so
  // the filter comes back on the next visit to /claims or page refresh.
  // Clearing the chip itself (isClaimFilterApplied false) is a real "forget
  // this" signal though, so that case removes the persisted entry entirely
  // rather than writing isApplied: false — otherwise a stale entry would
  // silently reapply the filter (e.g. in ClaimsTable) on the next refresh.
  // Closing the claim preview is the other real "forget this"; since it only
  // reaches here as another null, Claims.tsx's onCloseCallback clears it there.
  // filtersScope is deliberately not a dependency: a scope change on its own must not
  // re-save the previous scope's claim under the new scope's key. (A tenant change
  // reloads the page today anyway, see MainStore.setFiltersScope.)
  useEffect(() => {
    if (!filterByClaimId) {
      return;
    }
    if (isClaimFilterApplied) {
      savePersistedClaimFilter(filtersScope, { claimId: filterByClaimId, isApplied: true });
    } else {
      clearPersistedClaimFilter(filtersScope);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterByClaimId, isClaimFilterApplied]);

  const clearChat = () => {
    clearChatCategoryStore(true);
    setClaimToPreview(null);
    setContractToPreview(null);
    setPolicyToPreview(null);
    setIsPoliciesDocumentsDrawerOpen(false);
    setClaimActivePredefaultQuestion(null);
    chatStore.clearChatStates();
  };

  // Callback functions for immediate UI updates
  const handleChatDeleted = (chatName: string) => {
    deleteChannel(chatName);

    const currentPath = location.pathname;

    if (currentPath === "/" && isHomeV2Enabled) {
      chatStore.loadDashboardStatistics();
    }

    if (filterByClaimId) {
      setTempClaimNumber(filterByClaimId);
      navigate("/claims");
      clearChat();
      return;
    }

    const isCurrentChat =
      currentPath === `/channels/${chatName}` ||
      currentPath === `/newchannel/${chatName}`;

    if (isCurrentChat) {
      navigate("/");
      clearChat();
    }
  };

  const handleChatPinned = (chatName: string, isPinned: boolean) => {
    toggleChannelPin(chatName, isPinned);
  };

  const handleChatRenamed = (chatName: string, newTitle: string) => {
    renameChannel(chatName, newTitle);

    if (chatNotifications.has(chatName)) {
      setChatNotifications((prev) => {
        const newSet = new Set(prev);
        newSet.delete(chatName);
        newSet.add(newTitle);
        return newSet;
      });
    }
  };

  const handleChatClosed = useCallback(
    (chatName: string, closedAt?: string | null) => {
      closeChannel(chatName, closedAt ?? undefined);

      if (location.pathname === "/" && isHomeV2Enabled) {
        chatStore.loadDashboardStatistics();
      }
    },
    [closeChannel, location.pathname, isHomeV2Enabled, chatStore],
  );

  const handleChatShared = (chatId: string, isShared: boolean) => {
    toggleChannelShared(chatId, isShared);
  };

  const handleChatSelected = (chatId: string, claimId: string | undefined) => {
    // Clear notification when chat is selected
    setChatNotifications((prev) => {
      const newSet = new Set(prev);
      newSet.delete(chatId);
      return newSet;
    });
    // Emit the chatSelected event
    websocketService.emit("chatSelected", chatId);
    chatStore.setActiveChatId(chatId);
    chatStore.setIsChatLoading(true);
    chatStore.resetPreviousChat();
    setContractToPreview(null);
    setPolicyToPreview(null);
    setIsPoliciesDocumentsDrawerOpen(false);
    clearChatCategoryStore(true);
    chatStore.setClaimIdToPreview(null);
    // The previewed claim belongs to the chat/page we are leaving — keeping it
    // would leak another claim's details into the new chat (REV-2244).
    setClaimToPreview(null);
    // Manually reset in order to not have component flick
    setClaimActivePredefaultQuestion(null);
    // Clear previous chat data before loading next chat
    chatStore.clearChatStates();
    chatStore.setIsClaimDataLoading(true);
    chatStore.setIsChatLoading(true);
  };

  const handleReorderChannels = useCallback(
    (orderedChannels: IChannel[]) => {
      reorderChannels(orderedChannels);
    },
    [location?.pathname, filterByClaimId, reorderChannels],
  );

  const handleSearchResultClick = (result: ChatSearchResult) => {
    if (chatStore.activeChatId !== result.chat_id) {
      chatStore.setIsChatLoading(true);
    }

    // Keep search results visible, just navigate to the received chat
    navigate(`/channels/${result.chat_id}?messageId=${result.message.id}`);
  };

  // Listen for chat notifications
  useEffect(() => {
    const handleChatNotification = (data: {
      chatId: string;
      hasNewMessage: boolean;
    }) => {
      if (data.hasNewMessage) {
        setChatNotifications((prev) => new Set(prev).add(data.chatId));
      } else {
        // Clear notification when hasNewMessage is false
        setChatNotifications((prev) => {
          const newSet = new Set(prev);
          newSet.delete(data.chatId);
          return newSet;
        });
      }
    };

    websocketService.on(WS_EVENTS.CHAT_NOTIFICATION, handleChatNotification);

    return () => {
      websocketService.off(WS_EVENTS.CHAT_NOTIFICATION, handleChatNotification);
    };
  }, []);

  // Listen for title updates from server messages
  useEffect(() => {
    const handleTitleUpdate = (data: { chatId: string; title: string }) => {
      updateChannelTitle(data.chatId, data.title);
    };

    websocketService.on(WS_EVENTS.CHAT_TITLE_UPDATE, handleTitleUpdate);

    return () => {
      websocketService.off(WS_EVENTS.CHAT_TITLE_UPDATE, handleTitleUpdate);
    };
  }, [updateChannelTitle]);

  // Sync sidebar when backend reports a chat was closed (WS event or send error)
  useEffect(() => {
    const handleChatClosedEvent = (data: {
      chatId: string;
      closedAt?: string | null;
    }) => {
      handleChatClosed(data.chatId, data.closedAt);
    };

    websocketService.on(WS_EVENTS.CHAT_CLOSED, handleChatClosedEvent);

    return () => {
      websocketService.off(WS_EVENTS.CHAT_CLOSED, handleChatClosedEvent);
    };
  }, [handleChatClosed]);

  // When a claim is selected in the Claims table, scroll the search results
  // and shared chats panels back to top so the claim-filtered chats are visible.
  useEffect(() => {
    if (!filterByClaimId || location?.pathname !== "/claims") {
      return;
    }

    if (searchResultsScrollRef.current) {
      searchResultsScrollRef.current.scrollTop = 0;
    }
    sharedChatsRef.current?.scrollToTop();
    chatListScrollRef.current?.scrollToTop();
  }, [filterByClaimId, location?.pathname]);

  const handleMouseEnter = () => {
    if (isCollapsed) {
      setWasExpandedByHover(true);
      setCollapsed(false);
      setSidebarWidth(SIDEBAR_DEFAULT_WIDTH);
    }
  };

  const handleMouseLeave = () => {
    if (wasExpandedByHover) {
      setWasExpandedByHover(false);
      setCollapsed(true);
      setSidebarWidth(SIDEBAR_MIN_WIDTH);
    }
  };

  const handleToggleSidebar = () => {
    if (wasExpandedByHover) {
      // Pin icon clicked - pin the sidebar (keep it expanded)
      setWasExpandedByHover(false);
      setCollapsed(false);
    } else {
      // Normal toggle behavior
      toggleSidebar();
    }
  };

  const chatList = useMemo(() => {
    let list = chatChannels;

    // If we are in claims page, return the pinned claim chats and the chat channels that have a claim number
    if (!!filterByClaimId && isClaimFilterApplied) {
      list = list.filter((chat) => chat.claim_number === filterByClaimId);

      list =
        list?.slice().sort((a, b) => {
          const aTime = a.created_at ? new Date(a.created_at).getTime() : 0;
          const bTime = b.created_at ? new Date(b.created_at).getTime() : 0;

          return bTime - aTime;
        }) || [];
    }

    list = list?.filter((chat) =>
      chat.label?.toLowerCase()?.includes(searchQuery?.toLowerCase()),
    );

    // Applied even when the filters are otherwise at their defaults, so
    // "Hide closed chats" always takes effect.
    list = list?.filter((chat) =>
      matchesClosedFilter(chat, chatStore.sidebarFilters.hideClosedChats),
    );

    if (!isDefaultSidebarFilters(chatStore.sidebarFilters)) {
      list = applySidebarFilters(list, chatStore.sidebarFilters);
    }

    return list;
  }, [
    chatChannels,
    location?.pathname,
    filterByClaimId,
    isClaimFilterApplied,
    searchQuery,
    chatStore.sidebarFilters,
  ]);

  const sharedChats = useMemo(() => {
    let shared =
      chatChannels
        ?.filter((chat) => chat.user_email && chat.user_email !== user?.email)
        ?.map((chat) => ({ ...chat, pinned: false })) || [];

    if (filterByClaimId && isClaimFilterApplied) {
      shared = shared?.filter(
        (claim) => claim.claim_number === filterByClaimId,
      );
    }

    if (!!searchQuery) {
      shared = shared?.filter((chat) =>
        chat.label?.toLowerCase()?.includes(searchQuery?.toLowerCase()),
      );
    }

    // Applied even when the filters are otherwise at their defaults, so
    // "Hide closed chats" always takes effect.
    shared = shared?.filter((chat) =>
      matchesClosedFilter(chat, chatStore.sidebarFilters.hideClosedChats),
    );

    if (!isDefaultSidebarFilters(chatStore.sidebarFilters)) {
      shared = applySidebarFilters(shared, chatStore.sidebarFilters);
    }

    return shared;
  }, [
    chatChannels,
    user?.email,
    filterByClaimId,
    isClaimFilterApplied,
    searchQuery,
    location?.pathname,
    chatStore.sidebarFilters,
  ]);

  const { files: currentChatFiles } = chatStore.currentChatFiles;
  const hasDocuments =
    (currentChatFiles?.length ?? 0) > 0 || !!chatStore.latestChatLetter;
  const chatListLayoutKey = `${sharedChats.length > 0}:${hasDocuments}`;

  const chatListEmptyStateCopy =
    CHAT_LIST_EMPTY_STATE_COPY[
      sidebarSelectedTab === SIDEBAR_TAB_TYPES.pinned ? "pinned" : "all"
    ];

  if (location?.pathname === "/dashboard") {
    return null;
  }

  return (
    <>
      <ResizableDrawerWrapper>
        <AdaptiveDrawer>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              height: "100%",
              width: "100%",
              bgcolor: "white",
            }}
            onMouseEnter={isResizing || isMobile ? undefined : handleMouseEnter}
            onMouseLeave={isResizing || isMobile ? undefined : handleMouseLeave}
          >
            {/* Logo content at top - always visible, even in filter mode */}
            <SidebarLogoContent
              wasExpandedByHover={wasExpandedByHover}
              externalToggleSidebar={handleToggleSidebar}
            />

            {chatStore.isSidebarFilterModeEnabled ? (
              <SidebarFilterPanel />
            ) : (
              <>
            {/* Search - Fixed below logo */}
            <Fade in={isVisible} timeout={300}>
              <Box>
                <Row sx={{ alignItems: "center" }}>
                  <ChatSearch />
                  <SidebarFilterAction />
                </Row>
              </Box>
            </Fade>

            {!!searchQuery ? null : (
              <>
              <Fade in={isVisible} timeout={300}>
                <Box>
                  <SidebarChatTabs
                    sidebarSelectedTab={sidebarSelectedTab}
                    setSidebarSelectedTab={updateSidebarSelectedTab}
                    loading={loading}
                  />
                </Box>
              </Fade>
              
              {filterByClaimId && isClaimFilterApplied ? (
                <Fade in={isVisible} timeout={300}>
                  <Box sx={{ px: 2, pb: 1.5, width: "100%" }}>
                    <SidebarClaimFilterChip
                      isClaimFilterApplied={isClaimFilterApplied}
                      claimNumber={filterByClaimId}
                      onClear={() => setIsClaimFilterApplied(false)}
                      onReapply={() => setIsClaimFilterApplied(true)}
                    />
                  </Box>
                </Fade>
                ) : null}
              </>
            )}

            {/* Chat Lists - Scrollable area */}
            <Fade in={isVisible} timeout={300}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  width: "100%",
                  flex: 1,
                  minHeight: 0,
                  overflow: "hidden",
                }}
              >
                <Box
                  sx={{
                    position: "relative",
                    flex: 1,
                    minHeight: 0,
                    overflow: "hidden",
                    width: "100%",
                  }}
                >
                  {/* Search Results — always mounted */}
                  <Box
                    ref={searchResultsScrollRef}
                    sx={{
                      position: "absolute",
                      inset: 0,
                      overflowY: "auto",
                      overflowX: "hidden",
                      visibility: !!searchQuery ? "visible" : "hidden",
                      pointerEvents: !!searchQuery ? "auto" : "none",
                    }}
                  >
                    <SearchResultsRenderer
                      chatChannels={chatChannels}
                      onSearchResultClick={handleSearchResultClick}
                      onChatSelected={handleChatSelected}
                    />
                  </Box>

                  {/* Shared Chats — always mounted */}
                  <Box
                    sx={{
                      position: "absolute",
                      inset: 0,
                      display: "flex",
                      flexDirection: "column",
                      overflow: "hidden",
                      visibility:
                        !!!searchQuery &&
                        sidebarSelectedTab === SIDEBAR_TAB_TYPES.shared
                          ? "visible"
                          : "hidden",
                      pointerEvents:
                        !!!searchQuery &&
                        sidebarSelectedTab === SIDEBAR_TAB_TYPES.shared
                          ? "auto"
                          : "none",
                    }}
                  >
                    <SharedChatsContent
                      ref={sharedChatsRef}
                      sharedChats={sharedChats}
                      chatNotifications={chatNotifications}
                      onChatDeleted={handleChatDeleted}
                      onChatPinned={handleChatPinned}
                      onChatSelected={handleChatSelected}
                      loading={loading}
                      showClosedChatsHint={
                        chatStore.sidebarFilters.hideClosedChats
                      }
                    />
                  </Box>

                  {/* Chat List — always mounted */}
                  <Box
                    sx={{
                      position: "absolute",
                      inset: 0,
                      display: "flex",
                      flexDirection: "column",
                      overflow: "hidden",
                      visibility:
                        !!!searchQuery &&
                        sidebarSelectedTab !== SIDEBAR_TAB_TYPES.shared
                          ? "visible"
                          : "hidden",
                      pointerEvents:
                        !!!searchQuery &&
                        sidebarSelectedTab !== SIDEBAR_TAB_TYPES.shared
                          ? "auto"
                          : "none",
                    }}
                  >
                    <ChatListRenderer
                      ref={chatListScrollRef}
                      showPinned={
                        sidebarSelectedTab === SIDEBAR_TAB_TYPES.pinned
                      }
                      chatChannels={chatList}
                      chatNotifications={chatNotifications}
                      onChatDeleted={handleChatDeleted}
                      onChatPinned={handleChatPinned}
                      onChatSelected={handleChatSelected}
                      onReorderChannels={handleReorderChannels}
                      chatListRef={chatListRef}
                      loading={loading}
                      loadingMore={loadingMore}
                      layoutKey={chatListLayoutKey}
                      noDataTitleMsg={chatListEmptyStateCopy.title}
                      noDataSubtitleMsg={chatListEmptyStateCopy.subtitle}
                      showClosedChatsHint={
                        chatStore.sidebarFilters.hideClosedChats
                      }
                    />
                  </Box>
                </Box>
                <SidebarFileViewer />
              </Box>
            </Fade>

            {/* Spacer to push button to bottom when collapsed */}
            {!isVisible && <Box sx={{ flex: 1 }} />}

            {showHome ? (
              /* Footer - Fixed at bottom */
              <SidebarFooterContent />
            ) : null}
              </>
            )}
          </Box>
        </AdaptiveDrawer>
      </ResizableDrawerWrapper>

      <ShareChatModal onShareSuccess={handleChatShared} />
      <DeleteChatConfirmationDialog onChatDeleted={handleChatDeleted} />
      <CloseChatConfirmationDialog onChatClosed={handleChatClosed} />
      <RenameChatDialog
        isOpen={!!chatStore.showRenamePopup}
        title={
          chatStore.showRenamePopup?.label ||
          getChannelConfigForChat(chatStore.showRenamePopup).title ||
          "Chat"
        }
        chatId={chatStore.showRenamePopup?.name || ""}
        onConfirmCallback={(newTitle) => {
          if (chatStore.showRenamePopup) {
            handleChatRenamed(chatStore.showRenamePopup.name, newTitle);
          }
        }}
        onClose={() => chatStore.setShowRenamePopup(null)}
      />
    </>
  );
});

export default Sidebar;
